import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, Autocomplete,
  Alert, CircularProgress, Tooltip, Stack, Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  GppGood as SignIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Article as ReportIcon,
} from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import { useAuth } from '../contexts/AuthContext';
import { useClinicPageBreakpoints } from '../hooks/useClinicPageBreakpoints';
import { getLoggedInProfessionalOption, mergeLoggedInProfessionalIntoOptions } from '../utils/loggedInProfessional';
import { buildMedicalReportPrintHtml } from '../utils/medicalReportPrintDocument';
import { printClinicHtml } from '../utils/clinicPrintHtml';
import { isSignedMedicalReport } from '../utils/medicalReportSignature';
import { openSignedMedicalReportPdf } from '../services/documentSignatureService';
import {
  canDeleteEvolutionRecord,
  canInvalidateEvolutionRecord,
  evolutionDeleteTooltip,
  evolutionInvalidateTooltip,
  isEvolutionInvalidated,
} from '../utils/evolutionRecordPolicy';
import ClinicalHtmlEditor, { clinicalBodyIsEmpty } from './clinic/ClinicalHtmlEditor';
import ClinicalHtmlView from './clinic/ClinicalHtmlView';
import Cid10Autocomplete from './clinic/Cid10Autocomplete';
import SignPrescriptionDialog from './clinic/SignPrescriptionDialog';
import PrescriptionTemplateChipPicker from './clinic/PrescriptionTemplateChipPicker';
import DocumentTextTemplateManagerDialog from './clinic/DocumentTextTemplateManagerDialog';
import type { EvolutionAttachedPrintable } from './evolution/evolutionWorkflowTypes';
import { clinicalHtmlToPlainPreview } from '../utils/clinicalHtml';
import EvolutionInvalidationBanner from './evolution/EvolutionInvalidationBanner';
import EvolutionInvalidateDialog from './evolution/EvolutionInvalidateDialog';
import {
  clinicFormDialogRootSx,
  clinicLargeFormDialogPaperSx,
  mobileDialogFormContentSx,
  mobileFullScreenDialogPaperSx,
} from '../utils/mobileFormLayout';
import {
  CLOUD_API_THEME,
  cloudApiDialogHeaderSx,
  cloudApiDialogHeaderIconSx,
  cloudApiDialogContentSx,
  cloudApiDialogFooterSx,
  cloudApiDialogCancelButtonSx,
  cloudApiDialogPrimaryButtonSx,
  cloudApiOutlinedButtonSx,
  cloudApiInputFieldSx,
} from '../pages/whatsappCloudApiTheme';

export interface HealthcareMedicalReportDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  existingReportId?: string | null;
  onSaved?: () => void;
  /** Fluxo da evolução clínica — persiste o relatório e anexa aos impressos. */
  professionalId?: string;
  initialProfessional?: any;
  prefillTemplate?: any | null;
  existingClientKey?: string | null;
  onSuccess?: (item: EvolutionAttachedPrintable) => void;
}

const emptyForm = () => ({
  patient_id: '',
  professional_id: '',
  title: 'Relatório Médico',
  issue_date: new Date().toISOString().split('T')[0],
  body_text: '',
  diagnosis: '',
  diagnosis_code: '',
});

function reportInvalidateSubtitle(report?: any | null): string {
  if (!report) return '';
  const parts: string[] = [];
  if (report.issue_date) {
    parts.push(new Date(`${report.issue_date}T12:00:00`).toLocaleDateString('pt-BR'));
  }
  if (report.title) parts.push(String(report.title));
  const prof = report.professional_name || report.professional?.full_name;
  if (prof) parts.push(String(prof));
  return parts.join(' — ');
}

const HealthcareMedicalReportDialog: React.FC<HealthcareMedicalReportDialogProps> = ({
  open,
  onClose,
  patientId,
  patientName,
  existingReportId = null,
  onSaved,
  professionalId,
  initialProfessional,
  prefillTemplate = null,
  existingClientKey = null,
  onSuccess,
}) => {
  const evolutionMode = Boolean(onSuccess);
  const { user } = useAuth();
  const { isMobile } = useClinicPageBreakpoints();
  const viewer = { role: user?.role, professional_id: user?.professional_id };
  const canSelectProfessional = user?.role !== 'professional';

  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [loadedReport, setLoadedReport] = useState<any | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false);
  const [profSearch, setProfSearch] = useState('');
  const [profOptions, setProfOptions] = useState<any[]>([]);
  const lastProfRef = useRef('');

  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [invalidateTarget, setInvalidateTarget] = useState<any>(null);
  const [invalidating, setInvalidating] = useState(false);
  const [invalidateError, setInvalidateError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const reportId = existingReportId || loadedReport?.id || null;
  const activeReport = loadedReport;
  const formLocked = Boolean(
    activeReport && (isEvolutionInvalidated(activeReport) || isSignedMedicalReport(activeReport)),
  );

  const resetState = useCallback(() => {
    const next = emptyForm();
    next.patient_id = patientId;
    const me = initialProfessional?.id
      ? initialProfessional
      : getLoggedInProfessionalOption(user);
    if (professionalId) next.professional_id = professionalId;
    else if (me) next.professional_id = me.id;
    setForm(next);
    setLoadedReport(null);
    setProfOptions(me ? [me] : initialProfessional ? [initialProfessional] : []);
    setProfSearch('');
    lastProfRef.current = '';
    if (prefillTemplate && !existingReportId) {
      setForm((current) => ({
        ...current,
        title:
          current.title === 'Relatório Médico' && prefillTemplate?.name
            ? prefillTemplate.name
            : current.title,
        body_text: prefillTemplate?.body_text || current.body_text,
      }));
    }
  }, [existingReportId, initialProfessional, patientId, prefillTemplate, professionalId, user]);

  const loadTemplates = useCallback(async () => {
    try {
      const params: Record<string, string> = { category: 'report' };
      if (user?.professional_id) params.professional_id = user.professional_id;
      const { data } = await axios.get(getApiUrl('/clinic/document-text-templates'), { params });
      setTemplates(data.data || []);
    } catch {
      setTemplates([]);
    }
  }, [user?.professional_id]);

  const searchProfessionals = useCallback(async (q: string) => {
    try {
      const { data } = await axios.get(getApiUrl('/clinic/budgets-search/professionals'), {
        params: { search: q || '' },
      });
      const rows = data?.success !== false ? (data?.data || []) : [];
      setProfOptions(mergeLoggedInProfessionalIntoOptions(rows, user));
    } catch {
      setProfOptions(mergeLoggedInProfessionalIntoOptions([], user));
    }
  }, [user]);

  const loadExistingReport = useCallback(async (id: string) => {
    setLoadingExisting(true);
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/medical-reports/${id}`));
      const report = data.data;
      setLoadedReport(report);
      setForm({
        patient_id: report.patient_id || patientId,
        professional_id: report.professional_id || '',
        title: report.title || 'Relatório Médico',
        issue_date: report.issue_date || new Date().toISOString().split('T')[0],
        body_text: report.body_text || '',
        diagnosis: report.diagnosis || '',
        diagnosis_code: report.diagnosis_code || '',
      });
      const prof = report.professional || report.professional_snapshot;
      if (report.professional_id && prof) {
        setProfOptions(mergeLoggedInProfessionalIntoOptions([{
          id: report.professional_id,
          full_name: prof.full_name,
          registration_number: prof.registration_number,
          registration_type: prof.registration_type,
          registration_state: prof.registration_state,
          specialty: prof.specialty,
        }], user));
      }
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao carregar relatório', severity: 'error' });
    } finally {
      setLoadingExisting(false);
    }
  }, [patientId, user]);

  useEffect(() => {
    if (!open) return;
    resetState();
    void loadTemplates();
    void searchProfessionals('');
    if (existingReportId) {
      void loadExistingReport(existingReportId);
    }
  }, [open, existingReportId, resetState, loadTemplates, searchProfessionals, loadExistingReport]);

  useEffect(() => {
    if (!open || profSearch === lastProfRef.current) return;
    const timer = setTimeout(() => {
      lastProfRef.current = profSearch;
      void searchProfessionals(profSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [profSearch, open, searchProfessionals]);

  const applyTemplate = (template: any) => {
    setForm((f) => ({
      ...f,
      title: f.title === 'Relatório Médico' && template?.name ? template.name : f.title,
      body_text: template?.body_text || f.body_text,
    }));
  };

  const cleanPayload = () => ({
    patient_id: patientId,
    professional_id: form.professional_id || null,
    title: form.title?.trim() || 'Relatório Médico',
    issue_date: form.issue_date || new Date().toISOString().split('T')[0],
    body_text: form.body_text || '',
    diagnosis: form.diagnosis?.trim() || null,
    diagnosis_code: form.diagnosis_code?.trim() || null,
  });

  const handleSave = async () => {
    if (!form.title?.trim()) {
      setSnackbar({ open: true, message: 'Informe o título do relatório', severity: 'error' });
      return;
    }
    if (clinicalBodyIsEmpty(form.body_text)) {
      setSnackbar({ open: true, message: 'Escreva o conteúdo do relatório', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = cleanPayload();
      if (reportId) {
        const { data } = await axios.put(getApiUrl(`/clinic/medical-reports/${reportId}`), payload);
        const saved = data.data || { ...activeReport, ...payload };
        setLoadedReport(saved);
        if (evolutionMode && onSuccess) {
          onSuccess({
            clientKey: existingClientKey || crypto.randomUUID(),
            category: 'medical_report',
            templateId: prefillTemplate?.id,
            templateName: saved.title || payload.title,
            bodyText: clinicalHtmlToPlainPreview(payload.body_text, 500),
            linkedDocumentId: saved.id || reportId,
            linkedDocumentType: 'medical_report',
          });
          onClose();
          return;
        }
        setSnackbar({ open: true, message: 'Relatório atualizado!', severity: 'success' });
      } else {
        const { data } = await axios.post(getApiUrl('/clinic/medical-reports'), payload);
        const saved = data.data || null;
        setLoadedReport(saved);
        if (evolutionMode && onSuccess && saved?.id) {
          onSuccess({
            clientKey: existingClientKey || crypto.randomUUID(),
            category: 'medical_report',
            templateId: prefillTemplate?.id,
            templateName: saved.title || payload.title,
            bodyText: clinicalHtmlToPlainPreview(payload.body_text, 500),
            linkedDocumentId: saved.id,
            linkedDocumentType: 'medical_report',
          });
          onClose();
          return;
        }
        setSnackbar({ open: true, message: 'Relatório criado!', severity: 'success' });
      }
      onSaved?.();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!reportId) {
      setSnackbar({ open: true, message: 'Salve o relatório antes de imprimir', severity: 'error' });
      return;
    }
    if (isSignedMedicalReport(activeReport)) {
      try {
        await openSignedMedicalReportPdf(reportId, activeReport?.signed_pdf_url);
      } catch (e: any) {
        setSnackbar({ open: true, message: e?.message || 'Erro ao abrir PDF assinado', severity: 'error' });
      }
      return;
    }
    try {
      const { data } = await axios.get(getApiUrl(`/clinic/medical-reports/${reportId}/print`));
      printClinicHtml(buildMedicalReportPrintHtml(data.data));
    } catch {
      setSnackbar({ open: true, message: 'Erro ao imprimir', severity: 'error' });
    }
  };

  const openSignDialog = async () => {
    if (!reportId) {
      setSnackbar({ open: true, message: 'Salve o relatório antes de assinar', severity: 'error' });
      return;
    }
    if (!form.professional_id) {
      setSnackbar({ open: true, message: 'Defina o profissional responsável antes de assinar.', severity: 'error' });
      return;
    }
    if (!form.title?.trim()) {
      setSnackbar({ open: true, message: 'Informe o título do relatório', severity: 'error' });
      return;
    }
    if (clinicalBodyIsEmpty(form.body_text)) {
      setSnackbar({ open: true, message: 'Escreva o conteúdo do relatório', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = cleanPayload();
      const { data } = await axios.put(getApiUrl(`/clinic/medical-reports/${reportId}`), payload);
      const saved = data.data || { ...activeReport, ...payload };
      setLoadedReport(saved);
      onSaved?.();
      setSignDialogOpen(true);
    } catch (e: any) {
      setSnackbar({
        open: true,
        message: e.response?.data?.error || 'Erro ao salvar o relatório antes de assinar',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSigned = async (info: any) => {
    setSignDialogOpen(false);
    setSnackbar({
      open: true,
      message: `Relatório assinado. Código: ${info?.signature_verification_code || '—'}`,
      severity: 'success',
    });
    if (reportId) await loadExistingReport(reportId);
    onSaved?.();
    if (info?.id) {
      await openSignedMedicalReportPdf(info.id, info.signed_pdf_url).catch(() => undefined);
    }
  };

  const handleDelete = async () => {
    if (!reportId || !activeReport) return;
    if (!window.confirm('Deseja excluir este relatório médico?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/medical-reports/${reportId}`));
      setSnackbar({ open: true, message: 'Relatório excluído', severity: 'success' });
      onSaved?.();
      onClose();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao excluir', severity: 'error' });
    }
  };

  const handleConfirmInvalidate = async (reason: string) => {
    if (!reportId) return;
    setInvalidating(true);
    setInvalidateError(null);
    try {
      await axios.patch(getApiUrl(`/clinic/medical-reports/${reportId}/invalidate`), {
        invalidation_reason: reason,
      });
      setInvalidateTarget(null);
      setSnackbar({ open: true, message: 'Relatório invalidado', severity: 'success' });
      await loadExistingReport(reportId);
      onSaved?.();
    } catch (e: any) {
      setInvalidateError(e.response?.data?.error || 'Erro ao invalidar relatório');
    } finally {
      setInvalidating(false);
    }
  };

  const signDocument = activeReport
    ? { ...activeReport, patient_name: patientName, professional_id: form.professional_id }
    : null;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !saving && onClose()}
        maxWidth={false}
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { ...mobileFullScreenDialogPaperSx, overflow: 'hidden' }
            : {
                ...clinicLargeFormDialogPaperSx(false),
                borderTop: `3px solid ${CLOUD_API_THEME.teal}`,
              },
        }}
        sx={isMobile ? undefined : clinicFormDialogRootSx}
      >
        <DialogTitle sx={{ ...cloudApiDialogHeaderSx, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportIcon sx={cloudApiDialogHeaderIconSx} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {existingReportId || loadedReport ? 'Relatório Médico' : 'Novo Relatório Médico'}
              </Typography>
              <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>
                {patientName}
                {activeReport?.report_number ? ` · ${activeReport.report_number}` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            ...cloudApiDialogContentSx,
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            ...mobileDialogFormContentSx(isMobile),
          }}
        >
          {loadingExisting ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: CLOUD_API_THEME.teal }} />
            </Box>
          ) : (
            <>
              {activeReport && <EvolutionInvalidationBanner evolution={activeReport} sx={{ mb: 2 }} />}

              {activeReport && isSignedMedicalReport(activeReport) && !isEvolutionInvalidated(activeReport) && (
                <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                  Assinado digitalmente
                  {activeReport.signer_name ? ` por ${activeReport.signer_name}` : ''}
                  {activeReport.signature_verification_code
                    ? ` · Código ${activeReport.signature_verification_code}`
                    : ''}
                </Alert>
              )}

              {!reportId && (
                templates.length > 0 ? (
                  <>
                    <PrescriptionTemplateChipPicker
                      templates={templates}
                      onSelect={applyTemplate}
                      title="Modelos de relatório médico"
                      searchPlaceholder="Pesquisar modelo..."
                      footerExtra={(
                        <Button size="small" onClick={() => setTemplateManagerOpen(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                          Gerenciar modelos
                        </Button>
                      )}
                    />
                    <Divider sx={{ mb: 2.5 }} />
                  </>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }} action={(
                    <Button color="inherit" size="small" onClick={() => setTemplateManagerOpen(true)} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Criar modelo
                    </Button>
                  )}>
                    Nenhum modelo pré-preenchido cadastrado.
                  </Alert>
                )
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Paciente"
                    value={patientName}
                    disabled
                    InputProps={{ readOnly: true }}
                    sx={cloudApiInputFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    size="small"
                    disabled={formLocked || !canSelectProfessional}
                    options={profOptions}
                    getOptionLabel={(o: any) =>
                      `${o.full_name || ''} - ${o.registration_type || 'CRM'} ${o.registration_number || ''} ${o.registration_state || ''} ${o.specialty ? `(${o.specialty})` : ''}`}
                    onInputChange={(_, v, reason) => { if (reason !== 'reset') setProfSearch(v); }}
                    onChange={(_, v) => {
                      setForm((f) => ({ ...f, professional_id: v?.id || '' }));
                      if (v) { lastProfRef.current = ''; setProfSearch(''); }
                    }}
                    onOpen={() => {
                      if (profOptions.length === 0) { lastProfRef.current = ''; setProfSearch(''); }
                    }}
                    value={profOptions.find((p) => p.id === form.professional_id) || null}
                    renderInput={(params) => (
                      <TextField {...params} label="Profissional" placeholder="Buscar por nome ou registro..." sx={cloudApiInputFieldSx} />
                    )}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    filterOptions={(x) => x}
                    noOptionsText="Nenhum profissional encontrado"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Data de emissão"
                    value={form.issue_date}
                    disabled={formLocked}
                    onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    sx={cloudApiInputFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  {activeReport?.status && (
                    <Box sx={{ pt: 1 }}>
                      <Chip
                        size="small"
                        label={
                          activeReport.status === 'draft'
                            ? 'Rascunho'
                            : activeReport.status === 'signed'
                              ? 'Assinado'
                              : activeReport.status === 'cancelled'
                                ? 'Invalidado'
                                : activeReport.status
                        }
                        sx={{
                          fontWeight: 600,
                          bgcolor: activeReport.status === 'signed'
                            ? CLOUD_API_THEME.tealSoft
                            : activeReport.status === 'cancelled'
                              ? '#fef2f2'
                              : '#fffbeb',
                          color: activeReport.status === 'signed'
                            ? CLOUD_API_THEME.tealDark
                            : activeReport.status === 'cancelled'
                              ? '#ef4444'
                              : '#b45309',
                        }}
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Título"
                    value={form.title}
                    disabled={formLocked}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    sx={cloudApiInputFieldSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Cid10Autocomplete
                    value={form.diagnosis_code}
                    description={form.diagnosis}
                    disabled={formLocked}
                    onChange={(code, description) =>
                      setForm((f) => ({ ...f, diagnosis_code: code, diagnosis: description }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  {formLocked ? (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: CLOUD_API_THEME.tealDark }}>
                        Conteúdo do relatório
                      </Typography>
                      <ClinicalHtmlView html={form.body_text} />
                    </Box>
                  ) : (
                    <ClinicalHtmlEditor
                      label="Conteúdo do relatório"
                      value={form.body_text}
                      onChange={(html) => setForm((f) => ({ ...f, body_text: html }))}
                      minRows={16}
                      placeholder="Descreva o laudo clínico..."
                    />
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ ...cloudApiDialogFooterSx, flexWrap: 'wrap', gap: 1, flexShrink: 0 }}>
          {reportId && !loadingExisting && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button startIcon={<PrintIcon />} onClick={() => void handlePrint()} sx={cloudApiOutlinedButtonSx}>
                Imprimir
              </Button>
              {!isEvolutionInvalidated(activeReport) && !isSignedMedicalReport(activeReport) && (
                <Button
                  startIcon={<SignIcon />}
                  onClick={() => void openSignDialog()}
                  disabled={saving}
                  sx={cloudApiOutlinedButtonSx}
                >
                  Assinar
                </Button>
              )}
              {canInvalidateEvolutionRecord(viewer, activeReport) && (
                <Tooltip title={evolutionInvalidateTooltip(viewer, activeReport)}>
                  <Button
                    startIcon={<BlockIcon />}
                    onClick={() => { setInvalidateError(null); setInvalidateTarget(activeReport); }}
                    sx={{ ...cloudApiOutlinedButtonSx, color: '#ef4444', borderColor: '#fecaca' }}
                  >
                    Invalidar
                  </Button>
                </Tooltip>
              )}
              {canDeleteEvolutionRecord(user?.role, activeReport) && (
                <Tooltip title={evolutionDeleteTooltip(user?.role, activeReport)}>
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={() => void handleDelete()}
                    sx={{ ...cloudApiOutlinedButtonSx, color: '#ef4444', borderColor: '#fecaca' }}
                  >
                    Excluir
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving} sx={cloudApiDialogCancelButtonSx}>
            {formLocked ? 'Fechar' : 'Cancelar'}
          </Button>
          {!formLocked && !loadingExisting && (
            <Button
              variant="contained"
              onClick={() => void handleSave()}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={cloudApiDialogPrimaryButtonSx}
            >
              {saving ? 'Salvando...' : evolutionMode ? (reportId ? 'Atualizar e anexar' : 'Salvar e anexar à evolução') : reportId ? 'Atualizar' : 'Salvar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <SignPrescriptionDialog
        open={signDialogOpen}
        kind="medical_report"
        document={signDocument}
        onClose={() => setSignDialogOpen(false)}
        onSigned={(info) => void handleSigned(info)}
      />

      <EvolutionInvalidateDialog
        open={Boolean(invalidateTarget)}
        subtitle={reportInvalidateSubtitle(invalidateTarget)}
        submitting={invalidating}
        error={invalidateError}
        onClose={() => {
          if (invalidating) return;
          setInvalidateTarget(null);
          setInvalidateError(null);
        }}
        onConfirm={handleConfirmInvalidate}
      />

      <DocumentTextTemplateManagerDialog
        open={templateManagerOpen}
        onClose={() => setTemplateManagerOpen(false)}
        category="report"
        professionalId={user?.professional_id}
        onTemplatesChanged={() => void loadTemplates()}
        onApply={applyTemplate}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HealthcareMedicalReportDialog;
